import React from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

import Ariane, { TYPE_PUBLIC } from 'components/Ariane/Ariane';
import PageNotFound from 'components/Common/Errors/PageNotFound/PageNotFound';
import Loader from 'components/Common/Loader/Loader';
import Loading from 'components/Common/Loading/Loading';
import { ScrollToTop } from 'components/Common/ScrollToTop/ScrollToTop';
import CategoryProducts from 'components/Listing/CategoryProducts/CategoryProducts';
import ContentWithoutResult from 'components/Listing/ContentWithoutResult/ContentWithoutResult';
import GroupedProducts from 'components/Listing/GroupedProducts/GroupedProducts';
import useProductListing from 'hooks/listing/useProductListing';
import useScrollTop from 'hooks/useScrollTop';
import { getFullUri, getUri } from 'services/generic';
import { getBreadcrumbWithLocale } from 'services/octipas';
import { isBot } from 'services/ua';
import {
  TEMPLATE_BIG_PRODUCTS,
  TEMPLATE_DEFAULT,
  TEMPLATE_LARGE_SIZE,
  TEMPLATE_PAGE_LOOK,
} from 'settings/template';
import { PagerType, SeoType } from 'types/ProductListing/ProductListing';

import styles from './page.module.scss';

const TEMPLATE_COMPONENT = {
  [TEMPLATE_DEFAULT]: CategoryProducts,
  [TEMPLATE_BIG_PRODUCTS]: CategoryProducts,
  [TEMPLATE_LARGE_SIZE]: CategoryProducts,
  [TEMPLATE_PAGE_LOOK]: GroupedProducts,
};

export default function ProductListing(): JSX.Element {
  const { data, error, isValidating } = useProductListing();

  useScrollTop();

  if (error) {
    return (
      <>
        <ScrollToTop />
        <PageNotFound status={'500'} />
      </>
    );
  }

  if (!data) {
    return <Loader />;
  }

  const { breadcrumb, pager, productCount, seo, template } = data;
  const Component = TEMPLATE_COMPONENT[template];

  return (
    <div className={styles.container}>
      <RenderHelmet seo={seo} pager={pager} />
      <Ariane
        items={getBreadcrumbWithLocale(breadcrumb)}
        type={TYPE_PUBLIC}
        classes={{ nav: styles.nav }}
      />
      {productCount > 0 ? (
        <Component data={data} isDataValidating={isValidating} />
      ) : (
        <ContentWithoutResult title={seo.titleH1} />
      )}
      {isValidating && <Loading loading />}
    </div>
  );
}

function RenderHelmet({
  seo,
  pager,
}: {
  seo: SeoType;
  pager: PagerType;
}): JSX.Element {
  const { t } = useTranslation();
  const { canonical, metaDescription, title } = seo;
  const { nextPageUrl, previousPageUrl, currentPageNumber } = pager;

  return (
    <Helmet>
      <title>
        {t('META_TITLE_TEMPLATE', {
          title: t('CATEGORY_HEAD_META_TITLE_PAGE', {
            title: title || t('DEFAULT_HEAD_TITLE'),
            ...(isBot() && { count: currentPageNumber }),
          }),
        })}
      </title>
      <link rel="canonical" href={getFullUri(canonical)} />
      {previousPageUrl && <link rel="prev" href={getUri(previousPageUrl)} />}
      {nextPageUrl && <link rel="next" href={getUri(nextPageUrl)} />}
      <meta
        name="description"
        content={metaDescription || t('DEFAULT_HEAD_META_DESCRIPTION')}
      />
    </Helmet>
  );
}
